import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import Modal from '../../Modal/Modal';
import SellFromHomeTransactionDetails from '../../Modals/SellWizard/SellFromHomeTransactionDetails';
import SellFromHomeTransactionHistory from '../../Modals/SellWizard/SellFromHomeTransactionHistory';
import SfhModalTitle from '../../Modals/SellWizard/SfhModalTitle';
import { emptyPortfolioTransactionSingle } from '../../../store/actions/portfolio/portfolioActions';
import {
  fetchAccountSettings,
  fetchSingleAccount,
  setCurrentAccount,
} from '../../../store/actions/account/accountActions';
import {
  getCurrentAccountUid,
  selectAccounts,
} from '../../../store/selectors/accountSelectors';
import { useQuery } from '../../../util/hooks/useQuery';
import { PORTFOLIO_PAGE } from '../../../constants/pages';
import { ACCOUNT_UID } from '../../../constants/sessionStorage';
import { storeInSessionStorage } from '../../../util/helpers/sessionStorageHelper';
import { pxToRem } from '../../../assets/styles/helper';

// Opens the SFH "Transaction Details" wizard when the user arrives via an
// email deep link (`/portfolio?transactionuid=<uid>&accountcode=<code>`).
// Auth is handled upstream: PrivateRoute persists the full path (incl. query)
// and the post-login redirect restores it.
const SfhTransactionDeepLink = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const query = useQuery();
  const accounts = useSelector(selectAccounts);
  const currentAccountUid = useSelector(getCurrentAccountUid);
  const handledRef = useRef(false);

  // Backend deploys lowercase params; accept camelCase too.
  const deepLinkUid = query.transactionuid || query.transactionUid;
  const deepLinkAccountCode =
    query.accountcode || query.accountCode || query.AccountCode;

  const [sfhTransactionUid, setSfhTransactionUid] = useState(null);
  const [isSfhHistoryView, setIsSfhHistoryView] = useState(false);

  useEffect(() => {
    if (!deepLinkUid || handledRef.current) return;

    // Wait until accounts are loaded before deciding which one to select.
    if (!Array.isArray(accounts) || accounts.length === 0) return;

    // Strip query once we start handling so refresh/back does not re-open.
    history.replace(PORTFOLIO_PAGE);
    handledRef.current = true;

    const openDetails = () => setSfhTransactionUid(deepLinkUid);

    if (!deepLinkAccountCode) {
      // Legacy links without accountcode — keep prior behavior.
      openDetails();
      return;
    }

    const targetAccount = accounts.find(
      (account) =>
        account.AccountCode?.toLowerCase() ===
        String(deepLinkAccountCode).toLowerCase(),
    );

    // Account is not on this user (different email/session) — do not open.
    if (!targetAccount) return;

    if (currentAccountUid !== targetAccount.AccountUid) {
      storeInSessionStorage(ACCOUNT_UID, targetAccount.AccountUid);
      dispatch(setCurrentAccount(targetAccount));
      dispatch(fetchSingleAccount({ AccountUid: targetAccount.AccountUid }));
      dispatch(fetchAccountSettings({ AccountUid: targetAccount.AccountUid }));
    }

    openDetails();
  }, [
    deepLinkUid,
    deepLinkAccountCode,
    accounts,
    currentAccountUid,
    dispatch,
    history,
  ]);

  const handleClose = () => {
    dispatch(emptyPortfolioTransactionSingle());
    setSfhTransactionUid(null);
    setIsSfhHistoryView(false);
  };

  const handleViewHistory = () => setIsSfhHistoryView(true);
  const handleHistoryBack = () => setIsSfhHistoryView(false);

  return (
    <Modal
      isOpen={Boolean(sfhTransactionUid)}
      close={handleClose}
      goBack={isSfhHistoryView ? handleHistoryBack : undefined}
      title={
        <SfhModalTitle>
          {t(
            isSfhHistoryView
              ? 'sellWizard.sfhWizard.transactionHistory.title'
              : 'sellWizard.sfhWizard.transactionDetails.title',
          )}
        </SfhModalTitle>
      }
      headerBordered
      size="sm"
      maxWidth={pxToRem(640)}
      minHeight={pxToRem(640)}
      height={pxToRem(640)}
      clipContent
      closeIconVisible
      skipListeningToLocations
      dataCy="container-sfh-transaction-details"
    >
      {sfhTransactionUid &&
        (isSfhHistoryView ? (
          <SellFromHomeTransactionHistory
            lockedSnapshot={{ transactionUid: sfhTransactionUid }}
            handleClose={handleClose}
          />
        ) : (
          <SellFromHomeTransactionDetails
            lockedSnapshot={{ transactionUid: sfhTransactionUid }}
            handleClose={handleClose}
            handleViewHistory={handleViewHistory}
          />
        ))}
    </Modal>
  );
};

export default SfhTransactionDeepLink;
